import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import '../assets/guardianPlacement.css';
import Loader from '../components/Loader';

const GuardianPlacementStep1B = () => {
  const { propertyId, guardianId, roomId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Data state
  const [property, setProperty] = useState(null);
  const [guardian, setGuardian] = useState(null);
  const [room, setRoom] = useState(null);
  const [availableGuardians, setAvailableGuardians] = useState([]);
  const [selectedSecondGuardian, setSelectedSecondGuardian] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    second_guardian_id: '',
    payment_split: '50:50'
  });

  // Mock data for when API fails
  const getMockData = () => {
    const mockProperty = {
      id: parseInt(propertyId),
      property_id: "PROP001",
      name: "12 Savage Road",
      status_text: "Active",
      address_1: "12 Savage Road",
      postcode: "PL5 1BP"
    };

    const mockGuardian = {
      id: parseInt(guardianId),
      unique_guardian_number: "UGD001",
      full_name: "John Smith",
      status: "Documents Required",
      user: {
        mobile_number: "07123456789",
        email: "john.smith@example.com"
      }
    };

    const mockRoom = {
      id: parseInt(roomId),
      name: "Room 101",
      price: 500.00,
      key_number: 2,
      is_couple: true
    };

    const mockAvailableGuardians = [
      {
        id: 2,
        unique_guardian_number: "UGD002",
        full_name: "Jane Smith",
        status: "Documents Required",
        user: {
          mobile_number: "07123456790",
          email: "jane.smith@example.com"
        }
      },
      {
        id: 3,
        unique_guardian_number: "UGD003",
        full_name: "Bob Johnson",
        status: "Documents Required",
        user: {
          mobile_number: "07123456791",
          email: "bob.johnson@example.com"
        }
      },
      {
        id: 4,
        unique_guardian_number: "UGD004",
        full_name: "Alice Brown",
        status: "Documents Required",
        user: {
          mobile_number: "07123456792",
          email: "alice.brown@example.com"
        }
      }
    ];

    return { mockProperty, mockGuardian, mockRoom, mockAvailableGuardians };
  };

  useEffect(() => {
    loadStep1BData();
  }, [propertyId, guardianId, roomId]);

  const loadStep1BData = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/guardian-placement/step1b/${propertyId}/${guardianId}/${roomId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.log('API authentication failed, using mock data');
          const { mockProperty, mockGuardian, mockRoom, mockAvailableGuardians } = getMockData();
          setProperty(mockProperty);
          setGuardian(mockGuardian);
          setRoom(mockRoom);
          setAvailableGuardians(mockAvailableGuardians);
          setLoading(false);
          return;
        }
        
        // Handle 403 Forbidden - Role access denied
        if (response.status === 403) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.message || 'You don\'t have permission to access this page.';
          setError(errorMessage);
          toast.error(errorMessage);
          setLoading(false);
          return;
        }
        
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setProperty(data.property);
      setGuardian(data.guardian);
      setRoom(data.room);
      setAvailableGuardians(data.availableGuardians || []);
      setLoading(false);
    } catch (err) {
      console.error('Error loading step 1B data:', err);
      // Use mock data as fallback
      const { mockProperty, mockGuardian, mockRoom, mockAvailableGuardians } = getMockData();
      setProperty(mockProperty);
      setGuardian(mockGuardian);
      setRoom(mockRoom);
      setAvailableGuardians(mockAvailableGuardians);
      setLoading(false);
    }
  };

  const handleSecondGuardianChange = (e) => {
    const guardianId = e.target.value;
    setFormData(prev => ({ ...prev, second_guardian_id: guardianId }));
    
    const selectedGuardian = availableGuardians.find(g => g.id === parseInt(guardianId));
    setSelectedSecondGuardian(selectedGuardian);
  };

  const handlePaymentSplitChange = (e) => {
    setFormData(prev => ({ ...prev, payment_split: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.second_guardian_id) {
      toast.error('Please select a second guardian');
      return;
    }

    try {
      setSubmitting(true);
      
      // Laravel behavior: POST directly to step2 (not step1b)
      // The form in Laravel step1b POSTs to step2 WITHOUT secondGuardian in URL, only in body
      // Then navigates to step2 WITH secondGuardian in URL
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/guardian-placement/step2/${propertyId}/${guardianId}/${roomId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          second_guardian_id: formData.second_guardian_id,
          payment_split: formData.payment_split
        })
      });

      if (!response.ok) {
        throw new Error('Failed to proceed with couple assignment');
      }

      const data = await response.json();
      toast.success('Couple assignment configured successfully');
      
      // Navigate to step 2 with second guardian in URL (step2 will display payment info)
      navigate(`/guardian-placement/step2/${propertyId}/${guardianId}/${roomId}/${formData.second_guardian_id}`);
    } catch (err) {
      console.error('Error proceeding with couple assignment:', err);
      toast.error('Failed to proceed with couple assignment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loader />;
  
  // Display role access error prominently
  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" style={{ marginTop: '20px', padding: '20px' }}>
          <h4 style={{ marginTop: 0, marginBottom: '15px' }}>
            <i className="glyphicon glyphicon-exclamation-sign" style={{ marginRight: '10px' }}></i>
            Access Denied
          </h4>
          <p style={{ fontSize: '16px', marginBottom: '10px' }}>{error}</p>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
            This page is only accessible to users with the following roles:
            <ul style={{ marginTop: '10px', marginBottom: 0 }}>
              <li>SALES WITH GUARDIANS</li>
              <li>GUARDIAN-MANAGER</li>
              <li>GUARDIAN-MANAGER-WITH-MAINTENANCE</li>
            </ul>
          </p>
          <button 
            className="btn btn-primary" 
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }
  
  if (!property || !guardian || !room) return <div className="container py-5 text-danger">Data not found</div>;

  return (
    <div className="letting-agent-dashboard">
      {/* Header Section */}
      <section className="letting-agent__header" style={{ backgroundColor: '#12274e' }}>
        <div className="container">
          <div className="row js-property-viewing-of-user">
            <div className="col-md-12" style={{ paddingTop: '40px', paddingBottom: '40px' }}>
              <div className="wrapper">
                <h2 className="col-white">Placing Guardian - Step 1B</h2>
                <h4 className="col-white">Assign Couple to {property.name} - {room.name}</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section style={{ marginTop: '20px' }}>
        <div className="container">
          <div className="panel panel-default panel-standard">
            <div className="panel-body">
              <div className="container">
                <h3>Couple Assignment Configuration</h3>
                
                {/* Primary Guardian */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="panel panel-default">
                      <div className="panel-heading">
                        <h4>Primary Guardian</h4>
                      </div>
                      <div className="panel-body">
                        <dl className="list">
                          <dt>Name:</dt>
                          <dd>{guardian.full_name}</dd>
                          <dt>UGD:</dt>
                          <dd>{guardian.unique_guardian_number}</dd>
                          <dt>Status:</dt>
                          <dd>{guardian.status}</dd>
                        </dl>
                      </div>
                    </div>
                  </div>

                  {/* Room Details */}
                  <div className="col-md-6">
                    <div className="panel panel-default">
                      <div className="panel-heading">
                        <h4>Room Details</h4>
                      </div>
                      <div className="panel-body">
                        <dl className="list">
                          <dt>Room:</dt>
                          <dd>{room.name}</dd>
                          <dt>Price:</dt>
                          <dd>£{room.price}</dd>
                          <dt>Keys:</dt>
                          <dd>{room.key_number}</dd>
                          <dt>Couple Room:</dt>
                          <dd>{room.is_couple ? 'Yes' : 'No'}</dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Couple Configuration Form */}
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="panel panel-default">
                        <div className="panel-heading">
                          <h4>Select Second Guardian</h4>
                        </div>
                        <div className="panel-body">
                          <div className="form-group">
                            <label htmlFor="second_guardian_id">Second Guardian:</label>
                            <select
                              id="second_guardian_id"
                              className="form-control"
                              value={formData.second_guardian_id}
                              onChange={handleSecondGuardianChange}
                              required
                            >
                              <option value="">Select a second guardian</option>
                              {availableGuardians.map(guardian => (
                                <option key={guardian.id} value={guardian.id}>
                                  {guardian.full_name} ({guardian.unique_guardian_number}) - {guardian.status}
                                </option>
                              ))}
                            </select>
                          </div>

                          {selectedSecondGuardian && (
                            <div className="alert alert-info">
                              <h5>Selected Second Guardian:</h5>
                              <p><strong>Name:</strong> {selectedSecondGuardian.full_name}</p>
                              <p><strong>UGD:</strong> {selectedSecondGuardian.unique_guardian_number}</p>
                              <p><strong>Status:</strong> {selectedSecondGuardian.status}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Split Configuration */}
                  <div className="row">
                    <div className="col-md-12">
                      <div className="panel panel-default">
                        <div className="panel-heading">
                          <h4>Payment Split Configuration</h4>
                        </div>
                        <div className="panel-body">
                          <div className="form-group">
                            <label htmlFor="payment_split">Payment Split:</label>
                            <select
                              id="payment_split"
                              className="form-control"
                              value={formData.payment_split}
                              onChange={handlePaymentSplitChange}
                            >
                              <option value="50:50">50:50 (Equal split)</option>
                              <option value="60:40">60:40 (Primary pays more)</option>
                              <option value="40:60">40:60 (Secondary pays more)</option>
                              <option value="70:30">70:30 (Primary pays most)</option>
                              <option value="30:70">30:70 (Secondary pays most)</option>
                            </select>
                            <small className="form-text text-muted">
                              This determines how the room payment will be split between the two guardians.
                            </small>
                          </div>

                          {formData.payment_split && (
                            <div className="alert alert-info">
                              <h5>Payment Split Preview:</h5>
                              <p><strong>Total Room Price:</strong> £{room.price}</p>
                              <p><strong>Split:</strong> {formData.payment_split}</p>
                              {(() => {
                                const [split1, split2] = formData.payment_split.split(':').map(Number);
                                const amount1 = (split1 / 100) * room.price;
                                const amount2 = (split2 / 100) * room.price;
                                return (
                                  <>
                                    <p><strong>{guardian.full_name}:</strong> £{amount1.toFixed(2)} ({split1}%)</p>
                                    <p><strong>{selectedSecondGuardian?.full_name || 'Second Guardian'}:</strong> £{amount2.toFixed(2)} ({split2}%)</p>
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="row">
                    <div className="col-md-12 text-center">
                      <button
                        type="submit"
                        className="btn btn-lg btn-primary"
                        disabled={submitting || !formData.second_guardian_id}
                      >
                        {submitting ? 'Configuring...' : 'Proceed to Payment Step'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GuardianPlacementStep1B;












